import React, { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Upload, Download, Search, FileText, Image as ImageIcon, Trash2, Edit, Plus,
  Folder, FolderOpen, Package, X, Mail, Phone, Zap, Check, Send, AlertCircle,
  Car, Filter, ChevronUp, Save, ShoppingBag, DollarSign, Users, RefreshCw, Loader2,
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { compressImage } from "../utils/compressImage";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

const ShopTab = ({ ctx }) => {
  const {
    shopProducts, shopOrders, shopCategories,
    shopCustomerSearch, setShopCustomerSearch,
    selectedShopCustomer, setSelectedShopCustomer,
    showCategoryManager, setShowCategoryManager,
    categoryForm, setCategoryForm,
    editingCategory, setEditingCategory,
    shopProductForm, setShopProductForm,
    editingProduct, setEditingProduct,
    shopImageUrl, setShopImageUrl,
    shopSpecKey, setShopSpecKey,
    shopSpecValue, setShopSpecValue,
    importUrl, setImportUrl,
    importLoading,
    importVariants, setImportVariants,
    selectedVariant, setSelectedVariant,
    selectedOrder, setSelectedOrder,
    trackingNumber, setTrackingNumber,
    shopLoading, categoryLoading, orderStatusLoading, shopCustomers,
    handleImportPreview, handleImportFromUrl, handleSaveProduct, handleEditProduct,
    handleDeleteProduct, handleSaveCategory, handleDeleteCategory,
    handleAddShopImage, handleRemoveShopImage, handleAddSpec, handleRemoveSpec,
    handleUpdateOrderStatus, handleDeleteOrder,
  } = ctx;

  // Photo upload state — mirrors GalleryTab pattern. Auth via global axios interceptor.
  const shopFileRef = useRef(null);
  const [shopUploading, setShopUploading] = useState(false);

  const handleShopUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setShopUploading(true);
    try {
      const compressed = await compressImage(file);
      const fd = new FormData();
      fd.append("file", compressed);
      const res = await axios.post(`${API}/admin/shop/upload-image`, fd);
      // Store the relative /api/... URL so it survives a domain change.
      setShopProductForm((prev) => ({ ...prev, images: [...prev.images, res.data.url] }));
      toast.success("Photo ajoutée au produit");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec de l'upload");
    } finally {
      setShopUploading(false);
      e.target.value = "";
    }
  };

  return (
    <>
          <div className="space-y-6">
            {/* Import from URL */}
            <div className="bg-gradient-to-r from-[#FF3B30]/20 to-[#121212] border border-[#FF3B30]/30 rounded-lg p-6">
              <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <Download size={20} className="text-[#FF3B30]" />
                Importer depuis une URL
              </h3>
              <p className="text-white/60 text-sm mb-4">
                Collez le lien d'un produit (TurboSystems, etc.) pour importer automatiquement les informations.
              </p>
              <div className="flex gap-3">
                <Input
                  value={importUrl}
                  onChange={(e) => setImportUrl(e.target.value)}
                  className="bg-[#1C1C1E] border-white/10 text-white flex-1"
                  placeholder="https://www.turbosystems.info/shop/product/..."
                />
                <button
                  onClick={handleImportPreview}
                  disabled={importLoading}
                  className="bg-[#FF3B30] hover:bg-[#D63025] disabled:opacity-50 text-white px-6 py-2 rounded-lg font-bold flex items-center gap-2"
                >
                  {importLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                      Import...
                    </>
                  ) : (
                    <>
                      <Download size={18} />
                      Importer
                    </>
                  )}
                </button>
              </div>
              
              {/* Variant Selection */}
              {importVariants && importVariants.has_variants && (
                <div className="mt-4 p-4 bg-[#1C1C1E] border border-[#FF3B30]/30 rounded-lg">
                  <h4 className="text-white font-bold mb-2 flex items-center gap-2">
                    <Zap size={16} className="text-[#FF3B30]" />
                    {importVariants.name}
                  </h4>
                  <p className="text-white/60 text-sm mb-3">
                    Ce produit a {importVariants.variants.length} version(s). Sélectionnez celle à importer :
                  </p>
                  <div className="space-y-2 mb-4">
                    {importVariants.variants.map((variant, idx) => (
                      <label 
                        key={idx} 
                        className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all ${
                          selectedVariant === variant.name 
                            ? 'bg-[#FF3B30]/20 border border-[#FF3B30]' 
                            : 'bg-[#121212] border border-white/10 hover:border-white/30'
                        }`}
                      >
                        <input
                          type="radio"
                          name="variant"
                          value={variant.name}
                          checked={selectedVariant === variant.name}
                          onChange={(e) => setSelectedVariant(e.target.value)}
                          className="accent-[#FF3B30]"
                        />
                        <span className="text-white flex-1">{variant.name}</span>
                        {variant.price && (
                          <span className="text-[#FF3B30] font-bold">€{variant.price.toFixed(2)}</span>
                        )}
                      </label>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleImportFromUrl(selectedVariant)}
                      disabled={!selectedVariant || importLoading}
                      className="flex-1 bg-[#FF3B30] hover:bg-[#D63025] disabled:opacity-50 text-white px-4 py-2 rounded-lg font-bold flex items-center justify-center gap-2"
                    >
                      {importLoading ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                      ) : (
                        <>
                          <Check size={18} />
                          Importer cette version
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => { setImportVariants(null); setSelectedVariant(""); }}
                      className="bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-lg"
                    >
                      <X size={18} />
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Category Manager */}
            <div className="bg-[#121212] border border-white/10 rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-white flex items-center gap-2">
                  <FolderOpen size={20} className="text-[#FF3B30]" />
                  Gestion des catégories
                </h3>
                <button
                  onClick={() => setShowCategoryManager(!showCategoryManager)}
                  className="bg-[#FF3B30]/20 hover:bg-[#FF3B30]/30 text-[#FF3B30] px-4 py-2 rounded-lg font-bold flex items-center gap-2 transition-colors"
                >
                  {showCategoryManager ? (
                    <>
                      <ChevronUp size={18} />
                      Masquer
                    </>
                  ) : (
                    <>
                      <Plus size={18} />
                      Ajouter / Modifier
                    </>
                  )}
                </button>
              </div>
              
              {/* Quick view of categories count */}
              {!showCategoryManager && shopCategories.length > 0 && (
                <p className="text-white/60 text-sm">
                  {shopCategories.length} catégorie(s) : {shopCategories.map(c => c.name).join(', ')}
                </p>
              )}
              
              {showCategoryManager && (
                <div className="space-y-4">
                  {/* Add/Edit Category Form */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                    <div>
                      <Label className="text-white/70 text-sm">Identifiant (unique)</Label>
                      <Input
                        value={categoryForm.id}
                        onChange={(e) => setCategoryForm({...categoryForm, id: e.target.value})}
                        className="bg-[#1C1C1E] border-white/10 text-white"
                        placeholder="ex: turbos"
                        disabled={editingCategory}
                      />
                    </div>
                    <div>
                      <Label className="text-white/70 text-sm">Nom affiché</Label>
                      <Input
                        value={categoryForm.name}
                        onChange={(e) => setCategoryForm({...categoryForm, name: e.target.value})}
                        className="bg-[#1C1C1E] border-white/10 text-white"
                        placeholder="ex: Turbos"
                      />
                    </div>
                    <div>
                      <Label className="text-white/70 text-sm">Icône</Label>
                      <Select
                        value={categoryForm.icon}
                        onValueChange={(value) => setCategoryForm({...categoryForm, icon: value})}
                      >
                        <SelectTrigger className="bg-[#1C1C1E] border-white/10 text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#1C1C1E] border-white/10">
                          <SelectItem value="Package">📦 Package</SelectItem>
                          <SelectItem value="Gauge">🔘 Gauge</SelectItem>
                          <SelectItem value="Wrench">🔧 Wrench</SelectItem>
                          <SelectItem value="Cog">⚙️ Cog</SelectItem>
                          <SelectItem value="Car">🚗 Car</SelectItem>
                          <SelectItem value="Settings">⚙️ Settings</SelectItem>
                          <SelectItem value="Zap">⚡ Zap</SelectItem>
                          <SelectItem value="Tool">🛠️ Tool</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={handleSaveCategory}
                        disabled={categoryLoading}
                        className="bg-[#FF3B30] hover:bg-[#D63025] disabled:opacity-50 text-white px-4 py-2 rounded-lg font-bold flex items-center gap-2"
                      >
                        {categoryLoading ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                        ) : editingCategory ? (
                          <Save size={16} />
                        ) : (
                          <Plus size={16} />
                        )}
                        {editingCategory ? "Modifier" : "Ajouter"}
                      </button>
                      {editingCategory && (
                        <button
                          onClick={() => {
                            setEditingCategory(null);
                            setCategoryForm({ id: "", name: "", icon: "Package" });
                          }}
                          className="bg-white/10 hover:bg-white/20 text-white px-3 py-2 rounded-lg"
                        >
                          <X size={16} />
                        </button>
                      )}
                    </div>
                  </div>
                  
                  {/* Categories List */}
                  <div className="border border-white/10 rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-white/5">
                        <tr>
                          <th className="text-left text-white/60 text-sm py-2 px-3">ID</th>
                          <th className="text-left text-white/60 text-sm py-2 px-3">Nom</th>
                          <th className="text-left text-white/60 text-sm py-2 px-3">Icône</th>
                          <th className="text-right text-white/60 text-sm py-2 px-3">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {shopCategories.map((cat) => (
                          <tr key={cat.id} className="border-t border-white/5 hover:bg-white/5">
                            <td className="text-white/80 py-2 px-3 font-mono text-sm">{cat.id}</td>
                            <td className="text-white py-2 px-3">{cat.name}</td>
                            <td className="text-white/60 py-2 px-3">{cat.icon}</td>
                            <td className="py-2 px-3 text-right">
                              <button
                                onClick={() => {
                                  setEditingCategory(cat);
                                  setCategoryForm({ id: cat.id, name: cat.name, icon: cat.icon || "Package" });
                                }}
                                className="text-blue-400 hover:text-blue-300 mr-3"
                              >
                                <Edit size={16} />
                              </button>
                              <button
                                onClick={() => handleDeleteCategory(cat.id)}
                                className="text-red-400 hover:text-red-300"
                              >
                                <Trash2 size={16} />
                              </button>
                            </td>
                          </tr>
                        ))}
                        {shopCategories.length === 0 && (
                          <tr>
                            <td colSpan={4} className="text-center text-white/40 py-4">
                              Aucune catégorie. Cliquez sur l'onglet pour charger.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            {/* Product Form */}
            <div className="bg-[#121212] border border-white/10 rounded-lg p-6">
              <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <Package size={20} className="text-[#FF3B30]" />
                {editingProduct ? "Modifier le produit" : "Ajouter un produit"}
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <Label className="text-white/70">Nom *</Label>
                  <Input
                    value={shopProductForm.name}
                    onChange={(e) => setShopProductForm({...shopProductForm, name: e.target.value})}
                    className="bg-[#1C1C1E] border-white/10 text-white"
                    placeholder="Nom du produit"
                  />
                </div>
                <div>
                  <Label className="text-white/70">Marque</Label>
                  <Input
                    value={shopProductForm.brand}
                    onChange={(e) => setShopProductForm({...shopProductForm, brand: e.target.value})}
                    className="bg-[#1C1C1E] border-white/10 text-white"
                    placeholder="TurboSystems, Garrett..."
                  />
                </div>
                <div>
                  <Label className="text-white/70">Référence</Label>
                  <Input
                    value={shopProductForm.reference}
                    onChange={(e) => setShopProductForm({...shopProductForm, reference: e.target.value})}
                    className="bg-[#1C1C1E] border-white/10 text-white"
                    placeholder="SKU / Référence"
                  />
                </div>
                <div>
                  <Label className="text-white/70">Catégorie</Label>
                  <Select
                    value={shopProductForm.category}
                    onValueChange={(value) => setShopProductForm({...shopProductForm, category: value})}
                  >
                    <SelectTrigger className="bg-[#1C1C1E] border-white/10 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1C1C1E] border-white/10">
                      {shopCategories.length > 0 ? (
                        shopCategories.map((cat) => (
                          <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                        ))
                      ) : (
                        <>
                          <SelectItem value="turbos">Turbos</SelectItem>
                          <SelectItem value="kits">Kits & Stages</SelectItem>
                          <SelectItem value="accessoires">Accessoires</SelectItem>
                          <SelectItem value="pieces">Pièces détachées</SelectItem>
                        </>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-white/70">Prix (€) *</Label>
                  <Input
                    type="number"
                    value={shopProductForm.price}
                    onChange={(e) => setShopProductForm({...shopProductForm, price: e.target.value})}
                    className="bg-[#1C1C1E] border-white/10 text-white"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <Label className="text-white/70">Prix barré (€)</Label>
                  <Input
                    type="number"
                    value={shopProductForm.original_price}
                    onChange={(e) => setShopProductForm({...shopProductForm, original_price: e.target.value})}
                    className="bg-[#1C1C1E] border-white/10 text-white"
                    placeholder="Ancien prix (promo)"
                  />
                </div>
                <div>
                  <Label className="text-white/70">Frais de port (€)</Label>
                  <Input
                    type="number"
                    value={shopProductForm.shipping_cost}
                    onChange={(e) => setShopProductForm({...shopProductForm, shipping_cost: e.target.value})}
                    className="bg-[#1C1C1E] border-white/10 text-white"
                    placeholder="Ex: 45 pour turbos Lituanie"
                  />
                </div>
                <div>
                  <Label className="text-white/70">Stock</Label>
                  <Input
                    type="number"
                    value={shopProductForm.stock}
                    onChange={(e) => setShopProductForm({...shopProductForm, stock: e.target.value})}
                    className="bg-[#1C1C1E] border-white/10 text-white"
                    placeholder="Quantité"
                  />
                </div>
                <div className="flex items-center gap-4 pt-6">
                  <label className="flex items-center gap-2 text-white/70 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={shopProductForm.show_stock}
                      onChange={(e) => setShopProductForm({...shopProductForm, show_stock: e.target.checked})}
                      className="w-4 h-4 accent-[#FF3B30]"
                    />
                    Afficher le stock
                  </label>
                  <label className="flex items-center gap-2 text-white/70 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={shopProductForm.featured}
                      onChange={(e) => setShopProductForm({...shopProductForm, featured: e.target.checked})}
                      className="w-4 h-4"
                    />
                    Produit vedette
                  </label>
                  <label className="flex items-center gap-2 text-white/70 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={shopProductForm.active}
                      onChange={(e) => setShopProductForm({...shopProductForm, active: e.target.checked})}
                      className="w-4 h-4"
                    />
                    Actif
                  </label>
                </div>
              </div>

              <div className="mt-4">
                <Label className="text-white/70">Description courte</Label>
                <Input
                  value={shopProductForm.short_description}
                  onChange={(e) => setShopProductForm({...shopProductForm, short_description: e.target.value})}
                  className="bg-[#1C1C1E] border-white/10 text-white"
                  placeholder="Résumé en quelques mots"
                />
              </div>

              <div className="mt-4">
                <Label className="text-white/70">Description complète</Label>
                <textarea
                  value={shopProductForm.description}
                  onChange={(e) => setShopProductForm({...shopProductForm, description: e.target.value})}
                  className="w-full bg-[#1C1C1E] border border-white/10 text-white rounded-lg p-3 min-h-[100px]"
                  placeholder="Description détaillée du produit..."
                />
              </div>

              {/* Images */}
              <div className="mt-4">
                <Label className="text-white/70">Images</Label>
                <div className="flex flex-col sm:flex-row gap-2 mt-2">
                  <button
                    type="button"
                    onClick={() => shopFileRef.current?.click()}
                    disabled={shopUploading}
                    data-testid="shop-upload-btn"
                    className="inline-flex items-center justify-center gap-2 bg-[#FF3B30]/80 hover:bg-[#FF3B30] disabled:opacity-50 text-white font-bold px-4 py-2.5 rounded-md text-xs uppercase tracking-wider whitespace-nowrap transition-colors"
                  >
                    {shopUploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                    {shopUploading ? "Envoi…" : "Uploader une photo"}
                  </button>
                  <input
                    ref={shopFileRef}
                    type="file"
                    accept="image/*,.heic,.heif"
                    className="hidden"
                    onChange={handleShopUpload}
                    data-testid="shop-upload-input"
                  />
                  <Input
                    value={shopImageUrl}
                    onChange={(e) => setShopImageUrl(e.target.value)}
                    className="bg-[#1C1C1E] border-white/10 text-white flex-1 text-base md:text-sm"
                    style={{ minHeight: 44 }}
                    placeholder="… ou coller une URL https://…"
                    data-testid="shop-image-url"
                  />
                  <button
                    onClick={handleAddShopImage}
                    data-testid="shop-add-url-btn"
                    className="bg-[#FF3B30] hover:bg-[#D63025] text-white px-4 py-2 rounded-lg min-h-[44px]"
                    title="Ajouter l'URL"
                  >
                    <Plus size={18} />
                  </button>
                </div>
                <p className="text-white/30 text-[11px] mt-1.5">
                  📱 Photos compressées automatiquement à l'upload — max 15 Mo par image, format libre.
                </p>
                {shopProductForm.images.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {shopProductForm.images.map((img, idx) => (
                      <div key={idx} className="relative group">
                        <img
                          src={img.startsWith("http") ? img : `${BACKEND}${img}`}
                          alt=""
                          className="w-20 h-20 object-cover rounded-lg"
                        />
                        <button
                          onClick={() => handleRemoveShopImage(idx)}
                          data-testid={`shop-remove-image-${idx}`}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity"
                          title="Retirer cette image"
                        >
                          <X size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Specifications */}
              <div className="mt-4">
                <Label className="text-white/70">Caractéristiques techniques</Label>
                <div className="flex gap-2 mt-2">
                  <Input
                    value={shopSpecKey}
                    onChange={(e) => setShopSpecKey(e.target.value)}
                    className="bg-[#1C1C1E] border-white/10 text-white"
                    placeholder="Nouvelle caractéristique"
                  />
                  <Input
                    value={shopSpecValue}
                    onChange={(e) => setShopSpecValue(e.target.value)}
                    className="bg-[#1C1C1E] border-white/10 text-white"
                    placeholder="Valeur"
                  />
                  <button
                    onClick={handleAddSpec}
                    className="bg-[#FF3B30] hover:bg-[#D63025] text-white px-4 py-2 rounded-lg"
                    title="Ajouter"
                  >
                    <Plus size={18} />
                  </button>
                </div>
                {Object.keys(shopProductForm.specifications).length > 0 && (
                  <div className="space-y-2 mt-3 bg-[#1C1C1E] p-4 rounded-lg">
                    <p className="text-white/50 text-xs mb-2">Cliquez sur un champ pour le modifier</p>
                    {Object.entries(shopProductForm.specifications).map(([key, value]) => (
                      <div key={key} className="flex gap-2 items-center">
                        <Input
                          value={key}
                          onChange={(e) => {
                            const newSpecs = { ...shopProductForm.specifications };
                            const oldValue = newSpecs[key];
                            delete newSpecs[key];
                            if (e.target.value.trim()) {
                              newSpecs[e.target.value] = oldValue;
                            }
                            setShopProductForm({ ...shopProductForm, specifications: newSpecs });
                          }}
                          className="bg-[#0A0A0A] border-white/10 text-white flex-1"
                          placeholder="Caractéristique"
                        />
                        <Input
                          value={value}
                          onChange={(e) => {
                            setShopProductForm({
                              ...shopProductForm,
                              specifications: {
                                ...shopProductForm.specifications,
                                [key]: e.target.value
                              }
                            });
                          }}
                          className="bg-[#0A0A0A] border-white/10 text-white flex-1"
                          placeholder="Valeur"
                        />
                        <button 
                          onClick={() => handleRemoveSpec(key)} 
                          className="text-red-400 hover:text-red-300 p-2"
                          title="Supprimer"
                        >
                          <X size={18} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={handleSaveProduct}
                  disabled={shopLoading}
                  className="bg-[#FF3B30] hover:bg-[#D63025] disabled:opacity-50 text-white px-6 py-2 rounded-lg font-bold"
                >
                  {shopLoading ? "Enregistrement..." : (editingProduct ? "Mettre à jour" : "Ajouter le produit")}
                </button>
                {editingProduct && (
                  <button
                    onClick={() => {
                      setEditingProduct(null);
                      setShopProductForm({
                        name: "", description: "", short_description: "", category: "turbos",
                        brand: "", reference: "", price: "", original_price: "", shipping_cost: "", stock: "",
                        images: [], specifications: {}, featured: false, active: true, show_stock: true
                      });
                    }}
                    className="bg-white/10 hover:bg-white/20 text-white px-6 py-2 rounded-lg"
                  >
                    Annuler
                  </button>
                )}
              </div>
            </div>

            {/* Products List */}
            <div className="bg-[#121212] border border-white/10 rounded-lg p-6">
              <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <ShoppingBag size={20} className="text-[#FF3B30]" />
                Produits ({shopProducts.length})
              </h3>
              
              {shopProducts.length === 0 ? (
                <p className="text-white/40 text-center py-8">Aucun produit. Ajoutez votre premier produit ci-dessus.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead className="border-b border-white/10">
                      <tr className="text-white/60 text-sm">
                        <th className="py-3 px-2">Image</th>
                        <th className="py-3 px-2">Produit</th>
                        <th className="py-3 px-2">Catégorie</th>
                        <th className="py-3 px-2">Prix</th>
                        <th className="py-3 px-2">Stock</th>
                        <th className="py-3 px-2">Statut</th>
                        <th className="py-3 px-2">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="text-white">
                      {shopProducts.map((product) => (
                        <tr key={product.id} className="border-b border-white/5 hover:bg-white/5">
                          <td className="py-3 px-2">
                            {product.images?.[0] ? (
                              <img src={product.images[0]} alt="" className="w-12 h-12 object-cover rounded" />
                            ) : (
                              <div className="w-12 h-12 bg-white/10 rounded flex items-center justify-center">
                                <Package size={20} className="text-white/30" />
                              </div>
                            )}
                          </td>
                          <td className="py-3 px-2">
                            <p className="font-medium">{product.name}</p>
                            {product.brand && <p className="text-sm text-white/60">{product.brand}</p>}
                            {product.reference && <p className="text-xs text-white/40">Réf: {product.reference}</p>}
                          </td>
                          <td className="py-3 px-2 capitalize">{product.category}</td>
                          <td className="py-3 px-2">
                            <span className="text-[#FF3B30] font-bold">{product.price?.toFixed(2)} €</span>
                            {product.original_price > product.price && (
                              <span className="text-white/40 line-through text-sm ml-2">{product.original_price?.toFixed(2)} €</span>
                            )}
                          </td>
                          <td className="py-3 px-2">
                            <span className={product.stock > 0 ? "text-green-400" : "text-red-400"}>
                              {product.stock || 0}
                            </span>
                          </td>
                          <td className="py-3 px-2">
                            <span className={`px-2 py-1 rounded text-xs ${product.active ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"}`}>
                              {product.active ? "Actif" : "Inactif"}
                            </span>
                            {product.featured && (
                              <span className="ml-2 px-2 py-1 bg-yellow-500/20 text-yellow-400 rounded text-xs">Vedette</span>
                            )}
                          </td>
                          <td className="py-3 px-2">
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleEditProduct(product)}
                                className="text-blue-400 hover:text-blue-300"
                                title="Modifier"
                              >
                                <Edit size={18} />
                              </button>
                              <button
                                onClick={() => handleDeleteProduct(product.id)}
                                className="text-red-400 hover:text-red-300"
                                title="Supprimer"
                              >
                                <Trash2 size={18} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Orders List */}
            <div className="bg-[#121212] border border-white/10 rounded-lg p-6">
              <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <DollarSign size={20} className="text-green-400" />
                Commandes ({shopOrders.length})
              </h3>
              
              {shopOrders.length === 0 ? (
                <p className="text-white/40 text-center py-8">Aucune commande pour le moment.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead className="border-b border-white/10">
                      <tr className="text-white/60 text-sm">
                        <th className="py-3 px-2">N° Commande</th>
                        <th className="py-3 px-2">Client</th>
                        <th className="py-3 px-2">Articles</th>
                        <th className="py-3 px-2">Total TTC</th>
                        <th className="py-3 px-2">Paiement</th>
                        <th className="py-3 px-2">Statut</th>
                        <th className="py-3 px-2">Date</th>
                        <th className="py-3 px-2">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="text-white">
                      {shopOrders.map((order) => (
                        <tr key={order.id} className="border-b border-white/5 hover:bg-white/5">
                          <td className="py-3 px-2 font-mono text-sm">{order.id?.slice(0, 8).toUpperCase()}</td>
                          <td className="py-3 px-2">
                            <p className="font-medium">{order.customer_name}</p>
                            <p className="text-sm text-white/60">{order.customer_email}</p>
                          </td>
                          <td className="py-3 px-2">
                            {order.items?.map((item, idx) => (
                              <p key={idx} className="text-sm">{item.name} x{item.quantity}</p>
                            ))}
                          </td>
                          <td className="py-3 px-2 font-bold text-green-400">{(order.total_ttc || order.total || 0).toFixed(2)} €</td>
                          <td className="py-3 px-2">
                            <span className={`px-2 py-1 rounded text-xs ${
                              order.payment_status === "paid" ? "bg-green-500/20 text-green-400" :
                              order.payment_status === "failed" ? "bg-red-500/20 text-red-400" :
                              "bg-yellow-500/20 text-yellow-400"
                            }`}>
                              {order.payment_status === "paid" ? "Payé" : order.payment_status === "failed" ? "Échoué" : "En attente"}
                            </span>
                          </td>
                          <td className="py-3 px-2">
                            <span className={`px-2 py-1 rounded text-xs ${
                              order.status === "confirmed" ? "bg-blue-500/20 text-blue-400" :
                              order.status === "shipped" ? "bg-purple-500/20 text-purple-400" :
                              order.status === "delivered" ? "bg-green-500/20 text-green-400" :
                              order.status === "cancelled" ? "bg-red-500/20 text-red-400" :
                              "bg-yellow-500/20 text-yellow-400"
                            }`}>
                              {order.status === "confirmed" ? "Confirmé" :
                               order.status === "shipped" ? "Expédié" :
                               order.status === "delivered" ? "Livré" :
                               order.status === "cancelled" ? "Annulé" : "En attente"}
                            </span>
                            {order.tracking_number && (
                              <p className="text-xs text-white/50 mt-1">📦 {order.tracking_number}</p>
                            )}
                          </td>
                          <td className="py-3 px-2 text-sm text-white/60">
                            {order.created_at ? new Date(order.created_at).toLocaleDateString('fr-FR') : '-'}
                          </td>
                          <td className="py-3 px-2">
                            <button
                              onClick={() => {
                                setSelectedOrder(order);
                                setTrackingNumber(order.tracking_number || "");
                              }}
                              className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-sm"
                            >
                              Gérer
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Customers List */}
            <div className="bg-[#121212] border border-white/10 rounded-lg p-6">
              <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <Users size={20} className="text-blue-400" />
                Comptes clients ({shopCustomers.length})
              </h3>
              
              <div className="mb-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                  <input
                    type="text"
                    value={shopCustomerSearch}
                    onChange={(e) => {
                      setShopCustomerSearch(e.target.value);
                      fetchShopCustomers(e.target.value);
                    }}
                    placeholder="Rechercher un client (nom, email, téléphone)..."
                    className="w-full bg-[#1C1C1E] border border-white/10 rounded-lg pl-10 pr-4 py-2.5 text-white"
                  />
                </div>
              </div>
              
              {shopCustomers.length === 0 ? (
                <p className="text-white/40 text-center py-8">Aucun compte client pour le moment.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead className="border-b border-white/10">
                      <tr className="text-white/60 text-sm">
                        <th className="py-3 px-2">Client</th>
                        <th className="py-3 px-2">Email</th>
                        <th className="py-3 px-2">Téléphone</th>
                        <th className="py-3 px-2">Adresse</th>
                        <th className="py-3 px-2">Inscription</th>
                        <th className="py-3 px-2">Statut</th>
                        <th className="py-3 px-2">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="text-white">
                      {shopCustomers.map((customer) => (
                        <tr key={customer.id} className="border-b border-white/5 hover:bg-white/5">
                          <td className="py-3 px-2">
                            <p className="font-medium">{customer.first_name} {customer.last_name}</p>
                          </td>
                          <td className="py-3 px-2 text-sm">{customer.email}</td>
                          <td className="py-3 px-2 text-sm">{customer.phone || '-'}</td>
                          <td className="py-3 px-2 text-sm">
                            {customer.address ? (
                              <span>{customer.address}, {customer.postal_code} {customer.city}</span>
                            ) : (
                              <span className="text-white/40">-</span>
                            )}
                          </td>
                          <td className="py-3 px-2 text-sm text-white/60">
                            {customer.created_at ? new Date(customer.created_at).toLocaleDateString('fr-FR') : '-'}
                          </td>
                          <td className="py-3 px-2">
                            <span className={`px-2 py-1 rounded text-xs ${
                              customer.is_active !== false ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"
                            }`}>
                              {customer.is_active !== false ? "Actif" : "Désactivé"}
                            </span>
                          </td>
                          <td className="py-3 px-2">
                            <button
                              onClick={() => setSelectedShopCustomer(customer)}
                              className="inline-flex items-center gap-1.5 bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 border border-blue-500/20 px-3 py-1.5 rounded text-xs font-medium transition-colors"
                            >
                              <Edit size={12} /> Modifier
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              
              {/* Customer Detail / Edit Modal */}
              {selectedShopCustomer && (
                <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
                  <div className="bg-[#141414] border border-white/10 rounded-xl max-w-xl w-full my-8">
                    <div className="sticky top-0 bg-[#141414]/95 backdrop-blur border-b border-white/[0.06] p-4 flex justify-between items-center">
                      <h3 className="text-white font-semibold flex items-center gap-2">
                        <Edit size={16} className="text-blue-400" />
                        Modifier {selectedShopCustomer.first_name} {selectedShopCustomer.last_name}
                      </h3>
                      <button onClick={() => setSelectedShopCustomer(null)} className="text-white/60 hover:text-white">
                        <X size={20} />
                      </button>
                    </div>

                    <div className="p-5 space-y-4">
                      {/* Identité */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-white/50 text-[11px] uppercase tracking-wider mb-1.5 block">Prénom</label>
                          <input
                            type="text"
                            value={selectedShopCustomer.first_name || ''}
                            onChange={(e) => setSelectedShopCustomer({ ...selectedShopCustomer, first_name: e.target.value })}
                            className="w-full bg-[#1C1C1E] border border-white/[0.06] rounded-md px-3 py-2 text-white text-sm focus:outline-none focus:border-white/20"
                          />
                        </div>
                        <div>
                          <label className="text-white/50 text-[11px] uppercase tracking-wider mb-1.5 block">Nom</label>
                          <input
                            type="text"
                            value={selectedShopCustomer.last_name || ''}
                            onChange={(e) => setSelectedShopCustomer({ ...selectedShopCustomer, last_name: e.target.value })}
                            className="w-full bg-[#1C1C1E] border border-white/[0.06] rounded-md px-3 py-2 text-white text-sm focus:outline-none focus:border-white/20"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="text-white/50 text-[11px] uppercase tracking-wider mb-1.5 block">Email (lecture seule)</label>
                        <input
                          type="email"
                          value={selectedShopCustomer.email || ''}
                          readOnly
                          className="w-full bg-[#1C1C1E]/50 border border-white/[0.04] rounded-md px-3 py-2 text-white/50 text-sm cursor-not-allowed"
                        />
                      </div>

                      <div>
                        <label className="text-white/50 text-[11px] uppercase tracking-wider mb-1.5 block">Téléphone</label>
                        <input
                          type="tel"
                          value={selectedShopCustomer.phone || ''}
                          onChange={(e) => setSelectedShopCustomer({ ...selectedShopCustomer, phone: e.target.value })}
                          className="w-full bg-[#1C1C1E] border border-white/[0.06] rounded-md px-3 py-2 text-white text-sm focus:outline-none focus:border-white/20"
                        />
                      </div>

                      <div>
                        <label className="text-white/50 text-[11px] uppercase tracking-wider mb-1.5 block">Adresse</label>
                        <input
                          type="text"
                          value={selectedShopCustomer.address || ''}
                          onChange={(e) => setSelectedShopCustomer({ ...selectedShopCustomer, address: e.target.value })}
                          className="w-full bg-[#1C1C1E] border border-white/[0.06] rounded-md px-3 py-2 text-white text-sm focus:outline-none focus:border-white/20"
                        />
                      </div>

                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <label className="text-white/50 text-[11px] uppercase tracking-wider mb-1.5 block">Code postal</label>
                          <input
                            type="text"
                            value={selectedShopCustomer.postal_code || ''}
                            onChange={(e) => setSelectedShopCustomer({ ...selectedShopCustomer, postal_code: e.target.value })}
                            className="w-full bg-[#1C1C1E] border border-white/[0.06] rounded-md px-3 py-2 text-white text-sm focus:outline-none focus:border-white/20"
                          />
                        </div>
                        <div className="col-span-2">
                          <label className="text-white/50 text-[11px] uppercase tracking-wider mb-1.5 block">Ville</label>
                          <input
                            type="text"
                            value={selectedShopCustomer.city || ''}
                            onChange={(e) => setSelectedShopCustomer({ ...selectedShopCustomer, city: e.target.value })}
                            className="w-full bg-[#1C1C1E] border border-white/[0.06] rounded-md px-3 py-2 text-white text-sm focus:outline-none focus:border-white/20"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="text-white/50 text-[11px] uppercase tracking-wider mb-1.5 block">Pays</label>
                        <input
                          type="text"
                          value={selectedShopCustomer.country || 'France'}
                          onChange={(e) => setSelectedShopCustomer({ ...selectedShopCustomer, country: e.target.value })}
                          className="w-full bg-[#1C1C1E] border border-white/[0.06] rounded-md px-3 py-2 text-white text-sm focus:outline-none focus:border-white/20"
                        />
                      </div>

                      {/* Reset password (admin) */}
                      <div className="pt-3 border-t border-white/[0.06]">
                        <label className="text-white/60 text-xs uppercase tracking-wider mb-2 block">
                          Réinitialiser mot de passe
                        </label>
                        <div className="flex gap-2">
                          <input
                            data-testid={`shop-admin-reset-pwd-input-${selectedShopCustomer.id}`}
                            type="text"
                            placeholder="Nouveau mot de passe (min 6)"
                            value={selectedShopCustomer._new_password || ''}
                            onChange={(e) => setSelectedShopCustomer({ ...selectedShopCustomer, _new_password: e.target.value })}
                            className="flex-1 bg-[#1C1C1E] border border-white/[0.06] rounded-md px-3 py-2 text-white text-sm focus:outline-none focus:border-white/20"
                          />
                          <button
                            data-testid={`shop-admin-reset-pwd-submit-${selectedShopCustomer.id}`}
                            onClick={async () => {
                              const newPwd = selectedShopCustomer._new_password || '';
                              if (newPwd.length < 6) {
                                toast.error('Le mot de passe doit contenir au moins 6 caractères');
                                return;
                              }
                              try {
                                const fd = new FormData();
                                fd.append('new_password', newPwd);
                                await axios.put(`${API}/admin/shop/customers/${selectedShopCustomer.id}/password`, fd);
                                toast.success('Mot de passe réinitialisé');
                                setSelectedShopCustomer({ ...selectedShopCustomer, _new_password: '' });
                              } catch (err) {
                                toast.error(err.response?.data?.detail || 'Erreur');
                              }
                            }}
                            className="bg-[#FF3B30] hover:bg-[#D63025] text-white px-4 rounded-md text-sm font-semibold transition-colors"
                          >
                            Réinitialiser
                          </button>
                        </div>
                        <p className="text-white/30 text-[11px] mt-1.5">
                          Le client devra se reconnecter avec ce nouveau mot de passe.
                        </p>
                      </div>

                      {/* Infos readonly */}
                      <div className="pt-3 border-t border-white/[0.06] space-y-1.5 text-xs">
                        <div className="flex justify-between text-white/50">
                          <span>Inscrit le</span>
                          <span className="text-white/80">
                            {selectedShopCustomer.created_at ? new Date(selectedShopCustomer.created_at).toLocaleDateString('fr-FR') : '-'}
                          </span>
                        </div>
                        <div className="flex justify-between text-white/50">
                          <span>Commandes</span>
                          <span className="text-white/80">{selectedShopCustomer.total_orders || 0}</span>
                        </div>
                        <div className="flex justify-between text-white/50">
                          <span>Total dépensé</span>
                          <span className="text-emerald-400 font-medium">{(selectedShopCustomer.total_spent || 0).toFixed(2)} €</span>
                        </div>
                      </div>
                    </div>

                    <div className="sticky bottom-0 bg-[#141414]/95 backdrop-blur border-t border-white/[0.06] p-4 flex gap-2">
                      <button
                        onClick={() => setSelectedShopCustomer(null)}
                        className="flex-1 bg-white/[0.06] hover:bg-white/[0.1] text-white py-2 rounded-md text-sm transition-colors"
                      >
                        Annuler
                      </button>
                      <button
                        onClick={async () => {
                          try {
                            await axios.put(`${API}/admin/shop/customers/${selectedShopCustomer.id}`, {
                              first_name: selectedShopCustomer.first_name || '',
                              last_name: selectedShopCustomer.last_name || '',
                              phone: selectedShopCustomer.phone || '',
                              address: selectedShopCustomer.address || '',
                              city: selectedShopCustomer.city || '',
                              postal_code: selectedShopCustomer.postal_code || '',
                              country: selectedShopCustomer.country || 'France',
                            });
                            toast.success('Client mis à jour');
                            setSelectedShopCustomer(null);
                            // Refresh list if there's a fetcher exposed on ctx
                            if (ctx.fetchShopCustomers) ctx.fetchShopCustomers();
                          } catch (err) {
                            toast.error(err.response?.data?.detail || 'Erreur mise à jour');
                          }
                        }}
                        className="flex-1 bg-blue-500 hover:bg-blue-600 text-white py-2 rounded-md text-sm font-semibold transition-colors"
                      >
                        Enregistrer
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Order Management Modal */}
            {selectedOrder && (
                <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
                  <div className="bg-[#1C1C1E] border border-white/10 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                    <div className="sticky top-0 bg-[#1C1C1E] border-b border-white/10 p-4 flex justify-between items-center">
                      <h3 className="text-xl font-bold text-white">
                        Commande #{selectedOrder.id?.slice(0, 8).toUpperCase()}
                      </h3>
                      <button onClick={() => setSelectedOrder(null)} className="text-white/60 hover:text-white">
                        <X size={24} />
                      </button>
                    </div>
                    
                    <div className="p-6 space-y-6">
                      {/* Client Info */}
                      <div className="bg-white/5 rounded-lg p-4">
                        <h4 className="text-white font-bold mb-3">👤 Informations client</h4>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <p className="text-white/60">Nom</p>
                            <p className="text-white font-medium">{selectedOrder.customer_name}</p>
                          </div>
                          <div>
                            <p className="text-white/60">Email</p>
                            <p className="text-white font-medium">{selectedOrder.customer_email}</p>
                          </div>
                          <div>
                            <p className="text-white/60">Téléphone</p>
                            <p className="text-white font-medium">{selectedOrder.customer_phone || '-'}</p>
                          </div>
                          <div>
                            <p className="text-white/60">Adresse</p>
                            <p className="text-white font-medium">
                              {selectedOrder.customer_address}<br/>
                              {selectedOrder.customer_postal_code} {selectedOrder.customer_city}
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      {/* Order Items */}
                      <div className="bg-white/5 rounded-lg p-4">
                        <h4 className="text-white font-bold mb-3">📦 Articles commandés</h4>
                        {selectedOrder.items?.map((item, idx) => (
                          <div key={idx} className="flex justify-between py-2 border-b border-white/10 last:border-0">
                            <span className="text-white">{item.name} x{item.quantity}</span>
                            <span className="text-white/60">{(item.price_ht || item.price || 0).toFixed(2)} € HT</span>
                          </div>
                        ))}
                        <div className="mt-4 pt-4 border-t border-white/10 space-y-1">
                          <div className="flex justify-between text-sm">
                            <span className="text-white/60">Sous-total HT</span>
                            <span className="text-white">{(selectedOrder.subtotal_ht || 0).toFixed(2)} €</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-white/60">Frais de port HT</span>
                            <span className="text-white">{(selectedOrder.shipping_ht || 0).toFixed(2)} €</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-white/60">TVA (20%)</span>
                            <span className="text-white">{(selectedOrder.tva_amount || 0).toFixed(2)} €</span>
                          </div>
                          <div className="flex justify-between text-lg font-bold pt-2">
                            <span className="text-white">Total TTC</span>
                            <span className="text-green-400">{(selectedOrder.total_ttc || selectedOrder.total || 0).toFixed(2)} €</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Status Update */}
                      <div className="bg-white/5 rounded-lg p-4">
                        <h4 className="text-white font-bold mb-3">💳 Statut du paiement</h4>
                        <div className="flex items-center gap-3">
                          <span className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                            selectedOrder.payment_status === "paid" ? "bg-green-500/20 text-green-400" :
                            selectedOrder.payment_status === "failed" ? "bg-red-500/20 text-red-400" :
                            "bg-yellow-500/20 text-yellow-400"
                          }`}>
                            {selectedOrder.payment_status === "paid" ? "✓ Payé" : 
                             selectedOrder.payment_status === "failed" ? "✗ Échoué" : "⏳ En attente"}
                          </span>
                          {selectedOrder.payment_date && (
                            <span className="text-white/50 text-sm">
                              le {new Date(selectedOrder.payment_date).toLocaleString('fr-FR')}
                            </span>
                          )}
                        </div>
                        {selectedOrder.stripe_session_id && (
                          <p className="text-white/40 text-xs mt-2 font-mono">
                            Session Stripe: {selectedOrder.stripe_session_id.slice(0, 20)}...
                          </p>
                        )}
                        
                        {/* Resend payment link button */}
                        {selectedOrder.payment_status !== "paid" && (
                          <button
                            onClick={async () => {
                              try {
                                const response = await axios.post(`${API}/admin/shop/orders/${selectedOrder.id}/resend-payment`);
                                toast.success("Lien de paiement renvoyé par email !");
                                if (response.data.checkout_url) {
                                  // Optionally copy to clipboard
                                  navigator.clipboard.writeText(response.data.checkout_url);
                                  toast.success("Lien copié dans le presse-papier");
                                }
                              } catch (error) {
                                toast.error(error.response?.data?.detail || "Erreur lors de l'envoi du lien");
                              }
                            }}
                            className="mt-3 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2"
                          >
                            <RefreshCw size={16} />
                            Renvoyer le lien de paiement
                          </button>
                        )}
                      </div>
                      
                      {/* Shipping Management */}
                      <div className="bg-white/5 rounded-lg p-4">
                        <h4 className="text-white font-bold mb-3">🚚 Gestion de l'expédition</h4>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                          <div>
                            <label className="block text-white/60 text-sm mb-2">Transporteur</label>
                            <select
                              value={selectedOrder.carrier || ""}
                              onChange={(e) => setSelectedOrder({...selectedOrder, carrier: e.target.value})}
                              className="w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-4 py-2 text-white"
                            >
                              <option value="">Sélectionner...</option>
                              <option value="Colissimo">Colissimo</option>
                              <option value="Chronopost">Chronopost</option>
                              <option value="DPD">DPD</option>
                              <option value="UPS">UPS</option>
                              <option value="FedEx">FedEx</option>
                              <option value="Mondial Relay">Mondial Relay</option>
                              <option value="Autre">Autre</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-white/60 text-sm mb-2">Numéro de suivi</label>
                            <input
                              type="text"
                              value={trackingNumber}
                              onChange={(e) => setTrackingNumber(e.target.value)}
                              placeholder="Ex: 1Z999AA10123456784"
                              className="w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-4 py-2 text-white"
                            />
                          </div>
                        </div>
                        
                        <div className="flex flex-wrap gap-2">
                          {selectedOrder.status !== "shipped" && selectedOrder.payment_status === "paid" && (
                            <button
                              onClick={() => handleUpdateOrderStatus(selectedOrder.id, "shipped", trackingNumber, selectedOrder.carrier)}
                              disabled={orderStatusLoading || !trackingNumber}
                              className="bg-purple-600 hover:bg-purple-700 disabled:bg-purple-600/50 text-white px-4 py-2 rounded-lg font-medium"
                            >
                              {orderStatusLoading ? "Envoi..." : "📦 Marquer expédié & Envoyer suivi"}
                            </button>
                          )}
                          {selectedOrder.status === "shipped" && (
                            <button
                              onClick={() => handleUpdateOrderStatus(selectedOrder.id, "delivered")}
                              disabled={orderStatusLoading}
                              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium"
                            >
                              ✓ Marquer livré
                            </button>
                          )}
                          {selectedOrder.status !== "cancelled" && selectedOrder.status !== "delivered" && (
                            <button
                              onClick={() => handleUpdateOrderStatus(selectedOrder.id, "cancelled")}
                              disabled={orderStatusLoading}
                              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium"
                            >
                              Annuler
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteOrder(selectedOrder.id)}
                            className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg font-medium"
                          >
                            🗑️ Supprimer
                          </button>
                        </div>
                        
                        {selectedOrder.tracking_number && selectedOrder.status === "shipped" && (
                          <p className="mt-3 text-green-400 text-sm">
                            ✓ Email de suivi envoyé au client avec le numéro : {selectedOrder.tracking_number}
                          </p>
                        )}
                        
                        {selectedOrder.shipped_date && (
                          <p className="text-white/50 text-xs mt-2">
                            Expédié le {new Date(selectedOrder.shipped_date).toLocaleString('fr-FR')}
                          </p>
                        )}
                        {selectedOrder.delivered_date && (
                          <p className="text-white/50 text-xs mt-1">
                            Livré le {new Date(selectedOrder.delivered_date).toLocaleString('fr-FR')}
                          </p>
                        )}
                      </div>
                      
                      {/* Admin Notes */}
                      <div className="bg-white/5 rounded-lg p-4">
                        <h4 className="text-white font-bold mb-3">📝 Notes internes</h4>
                        <textarea
                          value={selectedOrder.admin_notes || ""}
                          onChange={(e) => setSelectedOrder({...selectedOrder, admin_notes: e.target.value})}
                          placeholder="Notes internes (non visibles par le client)..."
                          rows={3}
                          className="w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-4 py-2 text-white text-sm"
                        />
                        <button
                          onClick={async () => {
                            try {
                              const formData = new FormData();
                              formData.append('admin_notes', selectedOrder.admin_notes || '');
                              await axios.put(`${API}/admin/shop/orders/${selectedOrder.id}/notes`, formData);
                              alert('Notes sauvegardées');
                            } catch (error) {
                              alert('Erreur lors de la sauvegarde');
                            }
                          }}
                          className="mt-2 bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded text-sm"
                        >
                          Sauvegarder les notes
                        </button>
                      </div>
                      
                      {/* Order History */}
                      {selectedOrder.history && selectedOrder.history.length > 0 && (
                        <div className="bg-white/5 rounded-lg p-4">
                          <h4 className="text-white font-bold mb-3">📋 Historique</h4>
                          <div className="space-y-2">
                            {selectedOrder.history.map((entry, idx) => (
                              <div key={idx} className="flex items-start gap-3 text-sm">
                                <span className="text-white/40 whitespace-nowrap">
                                  {new Date(entry.date).toLocaleString('fr-FR')}
                                </span>
                                <span className="text-white">{entry.action}</span>
                                {entry.details && (
                                  <span className="text-white/60">- {entry.details}</span>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Creation Date */}
                      <div className="text-center text-white/40 text-xs">
                        Commande créée le {selectedOrder.created_at ? new Date(selectedOrder.created_at).toLocaleString('fr-FR') : '-'}
                      </div>
                    </div>
                  </div>
                </div>
              )}
          </div>
    </>
  );
};

export default ShopTab;
